import { useEffect, ReactNode, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: ReactNode
  children: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
  fullScreen?: boolean
  headerLeft?: ReactNode
  headerRight?: ReactNode
  onSubmit?: () => void
  submitLabel?: string
  submitting?: boolean
  mobileTopAligned?: boolean
  showDefaultCloseButton?: boolean
  mobileCloseVariant?: 'x' | 'back'
  closeLabel?: string
}

function CloseIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  )
}

function BackIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  )
}

function HeaderButton({
  onClick,
  title,
  children,
  disabled = false,
  variant = 'neutral',
}: {
  onClick: () => void
  title: string
  children: ReactNode
  disabled?: boolean
  variant?: 'neutral' | 'confirm'
}) {
  const className =
    variant === 'confirm'
      ? 'flex items-center gap-2 text-emerald-600 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-100 disabled:opacity-40 transition-colors'
      : 'flex items-center gap-2 text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white disabled:opacity-40 transition-colors'

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={className}
      title={title}
      aria-label={title}
    >
      {children}
    </button>
  )
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  fullScreen = false,
  headerLeft,
  headerRight,
  onSubmit,
  submitLabel = 'Submit',
  submitting = false,
  mobileTopAligned = true,
  showDefaultCloseButton = true,
  mobileCloseVariant = 'x',
  closeLabel = 'Close',
}: ModalProps) {
  const [isMobileView, setIsMobileView] = useState(false)
  const panelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const check = () => {
      setIsMobileView(typeof window !== 'undefined' ? window.innerWidth < 768 : false)
    }

    check()
    window.addEventListener('resize', check)

    return () => window.removeEventListener('resize', check)
  }, [])

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset'

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isMobileView) return
    if (e.key !== 'Enter') return

    const target = e.target as HTMLElement | null
    if (!target) return

    const tag = (target.tagName || '').toLowerCase()

    if (tag === 'textarea' || target.isContentEditable) return
    if (tag === 'button' || tag === 'a') return

    if (tag === 'input') {
      const inputType = (target.getAttribute('type') || '').toLowerCase()
      if (inputType === 'submit' || inputType === 'button' || inputType === 'image') return
    }

    const root = panelRef.current
    if (!root) return

    const focusableSelector =
      'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), input:not([type="hidden"]):not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'

    const focusableElements = Array.from(root.querySelectorAll<HTMLElement>(focusableSelector)).filter(
      (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0,
    )

    if (focusableElements.length === 0) return

    const active = document.activeElement as HTMLElement | null
    let currentIndex = focusableElements.indexOf(target)

    if (currentIndex === -1 && active) {
      currentIndex = focusableElements.indexOf(active)
    }

    if (currentIndex === -1) {
      currentIndex = 0
    }

    const direction = e.shiftKey ? -1 : 1
    let nextIndex = currentIndex + direction

    if (nextIndex < 0) nextIndex = focusableElements.length - 1
    if (nextIndex >= focusableElements.length) nextIndex = 0

    e.preventDefault()
    e.stopPropagation()

    focusableElements[nextIndex]?.focus()
  }

  if (!isOpen) return null

  const effectiveFullScreen = fullScreen || isMobileView

  const sizeClasses = isMobileView
    ? {
        sm: 'max-w-md',
        md: 'max-w-2xl',
        lg: 'max-w-4xl',
        xl: 'max-w-6xl',
      }
    : {
        sm: 'max-w-2xl',
        md: 'max-w-4xl',
        lg: 'max-w-6xl',
        xl: 'max-w-7xl',
      }

  const closeIcon = mobileCloseVariant === 'back' ? <BackIcon /> : <CloseIcon />

  const defaultCloseButton = showDefaultCloseButton ? (
    <HeaderButton onClick={onClose} title={closeLabel}>
      {closeIcon}
    </HeaderButton>
  ) : null

  const defaultSubmitButton = onSubmit ? (
    <HeaderButton
      onClick={onSubmit}
      disabled={submitting}
      title={submitLabel}
      variant="confirm"
    >
      <CheckIcon />
    </HeaderButton>
  ) : null

  const leftActions = headerLeft ?? (isMobileView ? defaultCloseButton : null)

  const rightActions =
    headerRight ??
    (onSubmit ? (
      <>
        {defaultSubmitButton}
        {!isMobileView && defaultCloseButton}
      </>
    ) : (
      !isMobileView ? defaultCloseButton : null
    ))

  const modalContent = (
    <>
      <div
        className="fixed inset-0 z-[9999] bg-black/50 dark:bg-black/70 transition-opacity"
        onClick={onClose}
      />

      <div
        className={`fixed inset-0 z-[10000] overflow-y-auto flex justify-center ${
          effectiveFullScreen ? 'p-0' : 'p-4'
        }`}
        style={{
          alignItems: isMobileView && mobileTopAligned ? 'flex-start' : 'center',
        }}
      >
        <div
          ref={panelRef}
          onKeyDown={handleKeyDown}
          tabIndex={-1}
          className={
            effectiveFullScreen
              ? 'relative w-full h-full sm:h-auto bg-white dark:bg-slate-800 shadow-2xl transform transition-all'
              : `relative w-full ${sizeClasses[size]} bg-white dark:bg-slate-800 rounded-xl shadow-2xl transform transition-all`
          }
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: fullScreen ? '100%' : undefined }}
        >
          <div className={fullScreen ? 'flex flex-col h-full' : ''}>
            <div
              className={`w-full ${
                fullScreen ? 'sticky top-0 z-30' : ''
              } bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border-b border-slate-200 dark:border-slate-700`}
            >
              <div className="relative flex items-center justify-between px-6 py-4">
                <div className="flex items-center gap-2">
                  {leftActions}
                </div>

                <div className="absolute left-1/2 transform -translate-x-1/2">
                  <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    {title}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {rightActions}
                </div>
              </div>
            </div>

            <div className={fullScreen ? 'overflow-y-auto p-6 flex-1' : 'p-6 overflow-y-auto max-h-[85vh]'}>
              {children}
            </div>
          </div>
        </div>
      </div>
    </>
  )

  return createPortal(modalContent, document.body)
}